/* global globalEvents */
import { fetchData, eventProps, postData } from 'helpers/global'
import { addClass, select, selectAll, removeClass } from 'helpers/dom'
import { hasOwnProperites } from 'helpers/utils'
import { getProductSubtitle } from 'uses/useShopify'

const selectors = {
  cartCount: '#CartCount',
  SECTION_STORAGE_AGE_VERIFICATION: 'age-verification',
  MINI_CART_CONTENT_CLASS: '.js-mini-cart-content',
  BTN_CART_CLEAR: '.js-btn-cart-clear'
}

const DISABLE_CHANGE_QUANTITY_PROPERTIES = ['byopAddon']

const customBadgeTags = window.SLS_STATE.customBadge.tags || []
const customBadgeItems = window.SLS_STATE.customBadge.items || []
const isLogged = window.SLS_STATE.customer.logged

const getCart = () => {
  return fetchData('/cart?view=ajax').then((cart) => {
    globalEvents.emit(eventProps.cart.count, cart.count)
    return cart
  })
}

const getLineItem = (id, properties, quantity) => {
  const data = {
    items: [
      {
        id,
        properties,
        quantity
      }
    ]
  }
  return data
}

const addCartItem = (id, properties = {}, quantity = 1) => {
  const lineItem = getLineItem(id, properties, quantity)
  return postData('/cart/add.js', lineItem, 'json', async (response) => {
    const { description } = await response.json()
    globalEvents.emit(eventProps.notice.global, {
      type: 'error',
      content: description
    })
  }).then((item) => {
    return item
  })
}

const addMultipleCartItems = (items) => {
  return postData('/cart/add.js', items, 'json', async (response) => {
    const { description } = await response.json()
    globalEvents.emit(eventProps.notice.global, {
      type: 'error',
      content: description
    })
  }).then((items) => {
    return items
  })
}

const updateMultipleCartItems = (items) => {
  return postData('/cart/update.js', items, 'json', async (response) => {
    const { description } = await response.json()
    globalEvents.emit(eventProps.notice.global, {
      type: 'error',
      content: description
    })
  }).then((items) => {
    return items
  })
}

const updateCartItem = (id, quantity) => {
  return postData('/cart/change.js', {
    id,
    quantity
  }).then((cart) => {
    globalEvents.emit(eventProps.cart.change, cart.items)
    return cart
  })
}

const changeQuantity = (id, quantity) => {
  return postData('/cart/change.js', {
    id,
    quantity
  }).then((cart) => {
    return cart
  })
}

const removeCartItem = (id) => {
  return postData('/cart/change.js', {
    id,
    quantity: 0
  }).then((cart) => {
    globalEvents.emit(eventProps.cart.change, cart.items)
    return cart
  })
}

const clearCart = () => {
  return postData('/cart/clear.js').then((cart) => {
    globalEvents.emit(eventProps.cart.clear, cart)
    return cart
  })
}

const reRenderCartCount = (count) => {
  const els = selectAll(selectors.cartCount, document.body)
  const btnCartClear = select(selectors.BTN_CART_CLEAR)

  if (els) {
    if (count === 0) {
      els.forEach(el => addClass('hidden', el))
      btnCartClear && addClass('hidden', btnCartClear)
    } else {
      els.forEach(el => {
        el.innerText = count
        removeClass('hidden', el)
        addClass('flex', el)
      })
      btnCartClear && removeClass('hidden', btnCartClear) && addClass('block', btnCartClear)
    }
  }
}

const getCartCount = (items) => {
  if (!items.length) {
    return 0
  }

  return items.reduce((total, item) => {
    return total + (item.quantity / getQuantityByCarton(item))
  }, 0)
}

const updateCart = async () => {
  const newCart = await getCart()
  reRenderCartCount(getCartCount(newCart.items))
  window.SLS_STATE.cart.initCart = newCart
  return newCart
}

const openMiniCart = () => {
  const el = select('cart-slideout modal-dialog', document.body)
  if (el) {
    el.show()
    const contentEl = select(selectors.MINI_CART_CONTENT_CLASS, el)
    if (contentEl) {
      contentEl.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth'
      })
    }
  }
}

const getCartBadges = (tags, properties) => {
  const items = []

  if (!tags.length && !properties) {
    return items
  }

  if (tags.length) {
    for (const tag of tags) {
      const index = customBadgeTags.findIndex(i => !tag.includes('preorder') && tag === i)
      if (index !== -1 && !customBadgeItems[index].hideOnCart) {
        items.push(
          customBadgeItems[index]
        )
      } else if (isLogged && tag.includes('members-discount:')) {
        const currentIndex = customBadgeTags.findIndex(i => i.includes('members-discount:'))
        if (currentIndex) {
          items.push(
            customBadgeItems[currentIndex]
          )
        }
      }
    }
  }

  if (properties && hasOwnProperites('Pre-Order', properties)) {
    if (properties['Pre-Order'] === 'true') {
      items.push({
        text: 'Pre-Order',
        tag: '',
        customClass: 'badge-accent-1',
        backgroundColor: '',
        textColor: ''
      })
    } else {
      items.push({
        text: properties['Pre-Order'],
        tag: '',
        customClass: 'badge-accent-1',
        backgroundColor: '',
        textColor: ''
      })
    }
  }

  return items.filter(item => item)
}

const getCartInfo = (item) => {
  const arrs = []
  if (item.product_title) {
    arrs.push({
      name: getProductSubtitle(item.product_title),
      key: null
    })
  }
  return arrs
}

const getCartProperties = (items) => {
  const arrs = []
  if (items && Object.keys(items).length > 0) {
    for (const [key, value] of Object.entries(items)) {
      if (key !== 'Pre-Order') {
        arrs.push({
          name: key,
          key: value
        })
      }
    }
  }
  return arrs
}

const getLocalAgeVerification = () => {
  const value = sessionStorage.getItem(selectors.SECTION_STORAGE_AGE_VERIFICATION)
  if (value && JSON.parse(value) === true) {
    return JSON.parse(value)
  } else {
    return false
  }
}

const setLocalAgeVerification = (value) => {
  sessionStorage.setItem(selectors.SECTION_STORAGE_AGE_VERIFICATION, JSON.stringify(value))
}

const getDisableChangeQuantity = (properties) => {
  if (properties.some(property => DISABLE_CHANGE_QUANTITY_PROPERTIES.includes(property.name))) {
    return true
  }
  return false
}

const checkProductPersonalised = (properties) => {
  return properties && properties.some(property => property.name === '_productType' && property.key === 'personalised-block')
}

const getItemsByKey = (items, item, key) => items.filter(cartItem => cartItem.properties && hasOwnProperites(key, cartItem.properties) && cartItem.properties.timestamp === item.properties.timestamp)

const getQuantityByCarton = (cartItem) => {
  return cartItem?.wholesale?.unitPerCarton || 1
}

const getMaxQuantity = (cartItem) => {
  const max = cartItem.inventory_management === 'shopify' && cartItem.inventory_policy === 'deny' && cartItem.inventory_quantity > 0
    ? cartItem.inventory_quantity
    : 1000
  return Math.floor(max / getQuantityByCarton(cartItem))
}

const getQuantity = (item) => {
  return item.quantity / getQuantityByCarton(item)
}

const removeBundlesItem = async (item) => {
  const newCart = await updateCart()
  const bundleItems = getItemsByKey(newCart.items, item, 'timestamp')
  if (bundleItems.length === 0) {
    return
  }
  const newUpdates = {}
  for (const itemCart of bundleItems) {
    newUpdates[itemCart.id] = 0
  }
  const body = {
    updates: newUpdates
  }
  await updateMultipleCartItems(body)
}

const changeQuantityBundlesProduct = async (item, quantity) => {
  if (quantity === 0) {
    await removeBundlesItem(item)
    return
  }

  const newCart = await updateCart()
  const getBundleItems = getItemsByKey(newCart.items, item, 'timestamp')

  if (getBundleItems.length === 0) {
    return
  }

  const bundleItems = getBundleItems.reverse()
  const newTimestamp = Date.now()
  const body = {
    items: bundleItems.map(i => {
      const newProperties = i.properties
      newProperties.timestamp = newTimestamp
      return {
        id: i.id,
        quantity: i.quantity,
        properties: newProperties
      }
    })
  }
  await addMultipleCartItems(body)
}

const validatePreOrder = (cart) => {
  let status = false
  const validatePreOrder = cart && cart.items.length && cart.items.map((item) => item && item.properties && Object.hasOwn(item.properties, 'Pre-Order'))
  if (validatePreOrder.length && validatePreOrder.some((item) => item)) {
    status = !validatePreOrder.reduce((result, item) => result && item)
  }
  return status
}

const hasProductInStore = (cart) => {
  return cart && cart.items.length && cart.items.some((item) => item && item.tags && item.tags.includes('in-store-only'))
}

const validateStoreOnly = (cart) => {
  let status = false
  const validateStoreOnly = cart && cart.items.length && cart.items.map((item) => item && item.tags && item.tags.includes('in-store-only'))
  if (validateStoreOnly.length && validateStoreOnly.some((item) => item)) {
    status = !validateStoreOnly.reduce((result, item) => result && item)
  }
  return status
}

const validateInStoreOnly = (cart) => {
  let status = false
  const validateInStoreOnly = cart && cart.items.length && cart.items.map((item) => item && item.properties && Object.hasOwn(item.properties, 'store-only'))
  if (validateInStoreOnly.length && validateInStoreOnly.some((item) => item)) {
    status = !validateInStoreOnly.reduce((result, item) => result && item)
  }
  return status
}

const validateGiftVoucher = (cart) => {
  return cart && cart.items.length && cart.items.some((item) => item && item.tags && item.tags.includes('giftvoucher'))
}

/**
 * Validates if pralines are being picked up from multiple locations
 * @param { {items: any[]} } cart
 * @returns { boolean }
 */
const validatePralineBoxMixedLocations = (cart) => {
  const pickupLocationPralineBoxes = (cart?.items || []).filter(x => x?.properties?.pickupLocationName).map(x => x.properties.pickupLocationName)
  const uniqueStores = [...new Set(pickupLocationPralineBoxes)]
  if (uniqueStores.length > 1) {
    return true
  }
  return false
}

/**
 * Validates if the cart has a mix of delivery and pickup locations for items
 * @param { {items: any[]} } cart
 * @returns { boolean }
 */
const validateMixedCart = (cart) => {
  const cartItemProperties = (cart?.items || []).map(item => item.properties ? Object.keys(item.properties) : null).filter(item => item)
  const pickUpItemOnly = cartItemProperties.every(item => item.includes('pickupLocationName'))
  const deliveryItemOnly = cartItemProperties.every(item => !item.includes('pickupLocationName'))
  if (!pickUpItemOnly && !deliveryItemOnly) {
    return true
  }
  return false
}

const klaviyoTrackingAddToCart = (newCart) => {
  // eslint-disable-next-line no-var
  var _learnq = window._learnq || []
  let cart = {
    total_price: newCart.total_price / 100,
    $value: newCart.total_price / 100,
    total_discount: newCart.total_discount,
    original_total_price: newCart.original_total_price / 100,
    items: newCart.items
  }
  if (window.item !== 'undefined') {
    cart = Object.assign(cart, window.item)
  }
  _learnq.push(['track', 'Added to Cart', cart])
}

export {
  getCart,
  addCartItem,
  updateCartItem,
  removeCartItem,
  clearCart,
  updateCart,
  openMiniCart,
  getCartBadges,
  getCartInfo,
  getCartProperties,
  getLocalAgeVerification,
  setLocalAgeVerification,
  addMultipleCartItems,
  getDisableChangeQuantity,
  checkProductPersonalised,
  updateMultipleCartItems,
  getItemsByKey,
  getMaxQuantity,
  getQuantityByCarton,
  getQuantity,
  removeBundlesItem,
  changeQuantityBundlesProduct,
  reRenderCartCount,
  validatePreOrder,
  hasProductInStore,
  validateStoreOnly,
  validateInStoreOnly,
  validateGiftVoucher,
  validatePralineBoxMixedLocations,
  validateMixedCart,
  klaviyoTrackingAddToCart,
  getCartCount,
  changeQuantity
}
